/**
 * Authentication client.
 *
 * The distinction that matters: `login` may return a **challenge** rather than
 * a session. A challenge token opens nothing — the API refuses it on every
 * other endpoint — so the OTP step cannot be skipped by a client that decides
 * not to show it.
 */

import { ApiError, request, setToken } from "./api";
import type { LoginResponse, User } from "./types";

export interface PasswordChanged {
  token: string;
  note: string;
}

export interface MfaEnrolResponse {
  secret: string;
  provisioning_uri: string;
  account: string;
}

export interface MfaConfirmResponse {
  recovery_codes: string[];
  note: string;
}

export interface MfaStatus {
  required: boolean;
  enrolled: boolean;
  confirmed_at: string | null;
  recovery_codes_remaining: number;
}

export interface UnenrolledApprover {
  user_id: number;
  name: string;
  email: string;
  role: string;
}

export const auth = {
  /**
   * Sign in. Stores the token either way, because the challenge token is what
   * the verify call needs — but a challenge grants nothing until exchanged.
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    const result = await request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: { email, password },
    });
    setToken(result.token);
    return result;
  },

  /** Exchange the challenge plus a code for a real session. */
  async verifyMfa(code: string): Promise<LoginResponse> {
    const result = await request<LoginResponse>("/api/auth/mfa/verify", {
      method: "POST",
      body: { code },
    });
    setToken(result.token);
    return result;
  },

  enrolMfa: () =>
    request<MfaEnrolResponse>("/api/auth/mfa/enrol", { method: "POST" }),

  confirmMfa: (code: string) =>
    request<MfaConfirmResponse>("/api/auth/mfa/confirm", {
      method: "POST",
      body: { code },
    }),

  mfaStatus: () => request<MfaStatus>("/api/auth/mfa/status"),

  unenrolledApprovers: () =>
    request<UnenrolledApprover[]>("/api/auth/mfa/unenrolled"),

  /** Your own display name. The server takes no id — only your own token. */
  updateName: (name: string) =>
    request<User>("/api/auth/me", { method: "PATCH", body: { name } }),

  /**
   * Change your own password.
   *
   * The server kills every outstanding token for the account, this one
   * included, and hands back a fresh one. Storing it is not a nicety: without
   * it the person who just typed their new password is signed out by their own
   * action, which reads as a failure.
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<PasswordChanged> {
    const result = await request<PasswordChanged>("/api/auth/password", {
      method: "POST",
      body: { current_password: currentPassword, new_password: newPassword },
    });
    setToken(result.token);
    return result;
  },
};

export { ApiError };
